// Talep Zaman Kuralları: yasak aralıkları haftalık/aylık takvimde renkli bloklar olarak gösterir
import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { WeekView, MonthView, agendaStyle, iso, addDays, useCalendarRange, CalendarToolbar } from './calendarViews';

const COLORS = { creation: '#0284c7', work: '#ea580c' };
const KIND_LABEL = { creation: 'A) Talep oluşturulamaz', work: 'B) Çalışma yapılamaz' };
const weekday = (d) => (new Date(`${d}T00:00:00`).getDay() + 6) % 7;

const toEvents = (rule, days) => {
  const out = [];
  const add = (d, s, e, part) => out.push({ id: `${rule.id}-${d}-${part}`, rule, start_date: d, end_date: d, start_time: s, end_time: e, location_name: '' });
  if (rule.mode === 'dates') {
    days.filter(d => d >= rule.date_start && d <= rule.date_end).forEach(d => add(d, '00:00', '24:00', 'd'));
    return out;
  }
  const overnight = rule.end_time <= rule.start_time;
  days.forEach(d => {
    if (rule.days.includes(weekday(d))) add(d, rule.start_time, overnight ? '24:00' : rule.end_time, 'a');
    const prev = iso(addDays(new Date(`${d}T00:00:00`), -1));
    if (overnight && rule.days.includes(weekday(prev))) add(d, '00:00', rule.end_time, 'b');
  });
  return out;
};

// Pastel kart; belirli tarihli kurallar hafif çizgili desenle ayrışır
const styleOf = (ev, color) => ({ ...agendaStyle(ev, color), ...(ev.rule.mode === 'dates' && { backgroundImage: `repeating-linear-gradient(45deg, ${color}1f 0 6px, transparent 6px 12px)` }) });

export const TimeRulesCalendar = ({ rules, locations, onEdit }) => {
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(new Date());
  const [kind, setKind] = useState('all');
  const [locationId, setLocationId] = useState('all');
  const days = useCalendarRange(view, anchor);
  const locName = (ids) => (ids.length ? ids.map(id => locations.find(l => l.id === id)?.name).filter(Boolean).join(', ') : 'Tüm lokasyonlar');
  const scope = (r) => [locName(r.location_ids), r.is_tipi.length ? r.is_tipi.join(', ') : 'Tüm iş tipleri', r.is_cesidi.length ? r.is_cesidi.join(', ') : 'Tüm iş çeşitleri'].join(' · ');

  const shown = rules.filter(r => r.is_active && (kind === 'all' || r.kind === kind) && (locationId === 'all' || !r.location_ids.length || r.location_ids.includes(locationId)));
  const events = shown.flatMap(r => toEvents(r, days));
  const tip = (ev) => (
    <div className="space-y-1 p-3 text-xs bg-white text-slate-700 rounded-md max-w-xs">
      <p className="font-semibold" style={{ color: COLORS[ev.rule.kind] }}>{KIND_LABEL[ev.rule.kind]}</p>
      <p className="text-slate-900">{ev.rule.auto_message}</p>
      {ev.rule.message && <p>Firma uyarısı: "{ev.rule.message}"</p>}
      <p className="text-slate-500">{scope(ev.rule)}</p>
      <p className="pt-1 text-[10px] text-slate-400">Düzenlemek için tıklayın</p>
    </div>
  );
  const props = {
    days, events, today: iso(new Date()), colorOf: (ev) => COLORS[ev.rule.kind], styleOf, tip, onPick: (ev) => onEdit(ev.rule),
    weekLines: (ev) => [`${ev.rule.kind === 'creation' ? 'A' : 'B'} · ${ev.rule.mode === 'dates' ? 'Tüm gün' : `${ev.rule.start_time}–${ev.rule.end_time}`}`, ev.rule.message || ev.rule.note, locName(ev.rule.location_ids)],
    monthLines: (ev) => [`${ev.rule.kind === 'creation' ? 'A' : 'B'} · ${ev.rule.mode === 'dates' ? 'Tüm gün' : `${ev.rule.start_time}–${ev.rule.end_time}`}`],
  };

  return (
    <div className="space-y-4" data-testid="time-rules-calendar">
      <CalendarToolbar view={view} setView={setView} anchor={anchor} setAnchor={setAnchor} days={days}>
        <div className="flex rounded-lg border p-0.5 bg-slate-50" data-testid="rule-cal-kind">
          {[['all', 'İkisi'], ['creation', 'A) Oluşturma'], ['work', 'B) Çalışma']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setKind(k)} data-testid={`rule-cal-kind-${k}`}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${kind === k ? 'bg-white shadow-sm font-medium text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}>{l}</button>
          ))}
        </div>
        <Select value={locationId} onValueChange={setLocationId}>
          <SelectTrigger className="w-52" data-testid="rule-cal-location"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm lokasyonlar</SelectItem>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
      </CalendarToolbar>
      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600" data-testid="rule-cal-legend">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ backgroundColor: COLORS.creation }} />A) Talep oluşturma yasağı</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ backgroundColor: COLORS.work }} />B) Çalışma yasağı</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border border-slate-400" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #94a3b8 0 3px, transparent 3px 6px)' }} />Tarih aralığı kuralı (tüm gün)</span>
        <span className="ml-auto text-slate-400" data-testid="rule-cal-count">{shown.length} aktif kural</span>
      </div>
      {view === 'week' ? <WeekView {...props} /> : <MonthView {...props} month={anchor.getMonth()} />}
    </div>
  );
};
